import { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { GET_MY_LOCALE, UPDATE_MY_LOCALE } from '@/queries/queries';
import { hasExplicitLocaleChoice, resolveLocaleSync, type UserLocale } from '@/lib/userLocale';

interface MyLocaleData {
  myData: { id: string; locale: string | null } | null;
}

// Keeps the account's language (used for emails) in sync with the UI language.
// Renders nothing; mounted once inside AuthProvider.
export const UserLocaleSync = () => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const client = useApolloClient();
  const accountLocaleRef = useRef<string | null | undefined>(undefined);
  const userId = user?.id;

  useEffect(() => {
    accountLocaleRef.current = undefined;
    if (!userId) return;

    let cancelled = false;

    const sync = async () => {
      // Not loaded yet: the initial fetch below will reconcile.
      if (accountLocaleRef.current === undefined) return;
      const { save, apply } = resolveLocaleSync({
        accountLocale: accountLocaleRef.current,
        uiLanguage: i18n.language,
        explicitChoice: hasExplicitLocaleChoice(),
      });
      if (apply) {
        await i18n.changeLanguage(apply);
      }
      if (save) {
        accountLocaleRef.current = save;
        await client.mutate<unknown, { locale: UserLocale }>({
          mutation: UPDATE_MY_LOCALE,
          variables: { locale: save },
        });
      }
    };

    const run = () => {
      sync().catch((error) => console.error('Failed to sync user locale:', error));
    };

    client
      .query<MyLocaleData>({ query: GET_MY_LOCALE, fetchPolicy: 'network-only' })
      .then(({ data }) => {
        if (cancelled || !data?.myData) return;
        accountLocaleRef.current = data.myData.locale;
        run();
      })
      .catch((error) => console.error('Failed to load user locale:', error));

    i18n.on('languageChanged', run);
    return () => {
      cancelled = true;
      i18n.off('languageChanged', run);
    };
  }, [userId, client, i18n]);

  return null;
};
